import {
  Group,
  HalfMoon,
  Language,
  LogOut,
  NavArrowRight,
  SunLight,
} from 'iconoir-react';
import { FC, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { ReversioIcon } from '@savgroup-front-common/core/protons/icons/Reversio.icon';
import { Separator } from '@savgroup-front-common/primitive/ui/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  useSidebar,
} from '@savgroup-front-common/primitive/ui/sidebar';

import { ROUTES } from '@/constants/routing';

import { useProfileMenuData } from '../../hooks/useProfileMenuData';
import { NavigationDrawerLanguageDrawer } from '../../NavigationDrawerLanguageDrawer/NavigationDrawerLanguageDrawer';
import { SelectSellerDrawer } from '../../SelectSeller/SelectSellerDrawer/SelectSellerDrawer';
import { MenuItem } from '../AppBarMenu/AppBarMenu.types';

import messages from './messages';
import { NavigationDrawerNavLink } from './NavigationDrawerNavLink/NavigationDrawerNavLink';
import { NavigationDrawerProfile } from './NavigationDrawerProfile/NavigationDrawerProfile';
import { NavigationDrawerRow } from './NavigationDrawerRow/NavigationDrawerRow';

interface NavigationDrawerProps {
  menus: MenuItem[];
}

export const NavigationDrawer: FC<NavigationDrawerProps> = ({ menus }) => {
  const [isSellerDrawerOpen, setIsSellerDrawerOpen] = useState(false);
  const [isLanguageDrawerOpen, setIsLanguageDrawerOpen] = useState(false);
  const { state, open, setOpen, isMobile, setOpenMobile } = useSidebar();
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    hasMultipleLanguages,
    hasSellerPicker,
    logout,
    isDarkModeEnabled,
    onToggleDarkMode,
  } = useProfileMenuData();

  const closeDrawer = (): void => {
    setOpen(false);
    setOpenMobile(false);
  };

  useEffect(() => {
    closeDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (!isMobile && open) {
      contentRef.current?.querySelector<HTMLElement>('a, button')?.focus();
    }
  }, [isMobile, open]);

  return (
    <>
      <div className="pointer-events-none fixed inset-x-0 top-12 bottom-0 z-50">
        {!isMobile && open && (
          <div
            aria-hidden="true"
            onClick={closeDrawer}
            className="pointer-events-auto fixed inset-x-0 top-12 bottom-0 bg-surface-overlay-default/50 duration-100"
          />
        )}

        <Sidebar
          collapsible="offcanvas"
          className="pointer-events-auto"
          style={{ top: '3rem' }}
          inert={state === 'collapsed' || undefined}
        >
          <SidebarHeader className="p-0 md:hidden">
            <Link
              to={ROUTES.HOME}
              onClick={closeDrawer}
              aria-label={safeFormattedIntlString(messages.goToHomePage)}
              className="flex h-12 w-full items-center bg-surface-brand-default px-4"
            >
              <ReversioIcon
                color="var(--color-text-contrast-default)"
                className="-translate-y-0.5"
              />
            </Link>
          </SidebarHeader>

          <SidebarContent
            ref={contentRef}
            className="gap-1 overflow-y-auto bg-surface-base-default p-2"
            data-testid="appBar_drawer"
          >
            <div className="md:hidden">
              <NavigationDrawerProfile />
              <Separator className="my-1" />
            </div>

            <nav>
              <SidebarMenu>
                {menus.map((menu) => (
                  <NavigationDrawerNavLink
                    key={menu.to}
                    menu={menu}
                    onNavigate={closeDrawer}
                  />
                ))}
              </SidebarMenu>
            </nav>

            <div className="flex w-full flex-col gap-1 md:hidden">
              <Separator className="my-1" />

              <SidebarMenu>
                {hasSellerPicker && (
                  <NavigationDrawerRow
                    icon={<Group />}
                    trailingIcon={<NavArrowRight />}
                    onClick={() => setIsSellerDrawerOpen(true)}
                    dataTestId="appBar_drawerSelectSeller"
                  >
                    {safeFormattedIntlString(messages.selectSeller)}
                  </NavigationDrawerRow>
                )}

                {hasMultipleLanguages && (
                  <NavigationDrawerRow
                    icon={<Language />}
                    trailingIcon={<NavArrowRight />}
                    onClick={() => setIsLanguageDrawerOpen(true)}
                    dataTestId="appBar_drawerLanguage"
                  >
                    {safeFormattedIntlString(messages.language)}
                  </NavigationDrawerRow>
                )}

                <NavigationDrawerRow
                  icon={
                    isDarkModeEnabled ? (
                      <SunLight />
                    ) : (
                      <HalfMoon />
                    )
                  }
                  onClick={onToggleDarkMode}
                  dataTestId="appBar_drawerToggleDarkMode"
                >
                  {isDarkModeEnabled
                    ? safeFormattedIntlString(messages.lightMode)
                    : safeFormattedIntlString(messages.darkMode)}
                </NavigationDrawerRow>
              </SidebarMenu>

              <Separator className="my-1" />

              <SidebarMenu>
                <NavigationDrawerRow
                  icon={<LogOut />}
                  onClick={logout}
                  dataTestId="appBar_drawerLogout"
                  variant="destructive"
                >
                  {safeFormattedIntlString(messages.logout)}
                </NavigationDrawerRow>
              </SidebarMenu>
            </div>
          </SidebarContent>
        </Sidebar>
      </div>

      <SelectSellerDrawer
        isOpen={isSellerDrawerOpen}
        onOpenChange={setIsSellerDrawerOpen}
        onSelected={closeDrawer}
        dataTestId="appBar_drawerSelectSellerDrawer"
      />

      <NavigationDrawerLanguageDrawer
        isOpen={isLanguageDrawerOpen}
        onOpenChange={setIsLanguageDrawerOpen}
        onSelected={closeDrawer}
      />
    </>
  );
};
